/**
 * ImageLightbox - Modal viewer for images with zoom, navigation, download,
 * touch swipe, pinch-zoom, thumbnail strip, and annotation overlay.
 * Used in report views for photo fields, service history and the unit gallery.
 *
 * TEXT (photo OCR). The "Text" button reads the words on the photo (server-side, cached) and lays a
 * selectable text layer over it in the same transformed box as the photo, so it follows zoom and
 * rotation; drag to select, copy, or "Copy all". On demand only: moving to another photo turns it
 * off, so browsing never spends a read. Swipe-navigation is off while it is on (it fights selection).
 *
 * Keys: Left/Right (previous/next), Home/End (first/last), +/- (zoom), Escape (close).
 * Rendered at the top of the page (portal), and Escape closes the viewer only: the drawers,
 * map panels and modals it opens from keep their own Escape for themselves.
 * Each photo fades in once its bytes have loaded, so moving between photos never flashes
 * a half-drawn image; the active thumbnail keeps itself scrolled into view.
 *
 * DRAWING (gallery F-6). `overlay(index)` renders a layer exactly over the loaded photo, with the
 * SAME rotate/zoom transform as the photo, so a drawing stays on the feature it marks. While
 * `editing` is true the viewer steps aside for the drawing editor: no swipe, pinch, arrow-key,
 * strip or backdrop-click navigation (any of those would lose or misread a stroke), no transform
 * animation, and `editingPanel` replaces the toolbar. The editor panel is aria-modal, so the
 * viewer's own key handling (ownsKey) yields to it.
 */

import { useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Download,
  ZoomIn,
  RotateCcw,
  Scan,
  PanelRight,
  ZoomOut,
  RotateCw,
  Info,
  ExternalLink,
  ScanText,
  Copy,
  RefreshCw,
} from 'lucide-react';
import type { FormSubmissionPhotoAnnotation } from '../../../types/forms';
import { focusableWithin } from '../../shared/ResponsiveOverlay/useOverlayA11y';
import { useOcrText } from './ocr/useOcrText';
import { OcrTextLayer } from './ocr/OcrTextLayer';
import { usePhotoViewport } from '../../shared/photoViewer/usePhotoViewport';
import { PhotoMetaDrawer, type PhotoMetaRow } from '../../shared/photoViewer/PhotoMetaDrawer';
import { actualSizeZoom, percentOfActual, viewTransform } from '../../shared/photoViewer/viewport';

// ============================================================================
// Types
// ============================================================================

export interface LightboxImage {
  src: string;
  alt?: string;
  annotation?: FormSubmissionPhotoAnnotation;
  driveLink?: string;
  /** Extra detail under the image, e.g. a gallery photo's provenance line. */
  caption?: ReactNode;
  /** Short text for the strip tile while `src` is empty (e.g. the photo's date); else its number. */
  thumbLabel?: string;
  /**
   * The Storage path of the ORIGINAL behind this photo, when the host knows it (its `src` may be a
   * blob: URL). The "Text" button sends the path instead of uploading the pixels; omit it and the
   * viewer works it out from a Firebase Storage URL, or sends the photo's bytes.
   */
  ocrPath?: string;
  /**
   * Details for the "Details" side drawer: only rows the host actually has (name, type, size, modified, who/when...). The viewer
   * adds the photo's pixel size itself once it loads. Absent = the drawer says no details are available.
   */
  meta?: readonly PhotoMetaRow[];
}

/** Natural pixel size of a decoded photo. */
export interface PhotoSize {
  w: number;
  h: number;
}

/**
 * The photo's on-screen box at zoom 1: the largest size that fits `box` once turned by `rotation`
 * (a quarter turn swaps the sides), never larger than the photo itself. Zoom scales this box.
 */
export function fitPhotoSize(natural: PhotoSize, rotation: number, box: PhotoSize): { width: number; height: number } {
  const quarter = rotation % 180 !== 0;
  const onScreenW = quarter ? natural.h : natural.w;
  const onScreenH = quarter ? natural.w : natural.h;
  const scale = Math.min(1, box.w / onScreenW, box.h / onScreenH);
  return { width: Math.round(natural.w * scale), height: Math.round(natural.h * scale) };
}

/** The viewing box — the same 90vw x (100vh - 160px) limit the photo's frame has always had. */
function viewingBox(): PhotoSize {
  return { w: window.innerWidth * 0.9, h: Math.max(0, window.innerHeight - 160) };
}

interface ImageLightboxProps {
  images: LightboxImage[];
  initialIndex?: number;
  onClose: () => void;
  /** Told whenever the shown image changes (a caller loading full-size bytes lazily). */
  onIndexChange?: (index: number) => void;
  /** Extra toolbar buttons for the shown image (e.g. the planned "Where was this taken?" map button). */
  toolbarExtra?: (index: number) => ReactNode;
  /** A layer drawn exactly over the loaded image, sharing its rotate/zoom (gallery F-6 drawings). */
  overlay?: (index: number) => ReactNode;
  /** True while a drawing editor is open: navigation, gestures and backdrop-close are off. */
  editing?: boolean;
  /** Shown in place of the toolbar while `editing` (the editor's own controls). */
  editingPanel?: ReactNode;
  /**
   * Called when the image on screen has actually LOADED, with the src that loaded. A host that
   * swaps a thumbnail for a sharper copy uses it to know the sharp copy is really showing.
   */
  onImageLoad?: (index: number, src: string) => void;
  /**
   * Hide the "Text" button. For a host whose photo is a local copy (a blob: URL the browser's connect policy will not let the
   * viewer re-read) or a document that must not be sent to the text-reading service (an email attachment can be an ID or a cheque).
   */
  disableOcr?: boolean;
}

// ============================================================================
// Annotation overlay helpers
// ============================================================================

const CONDITION_CONFIG = {
  good: { label: 'Good', bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500' },
  fair: { label: 'Fair', bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500' },
  poor: { label: 'Poor', bg: 'bg-orange-100', text: 'text-orange-800', dot: 'bg-orange-500' },
  critical: { label: 'Critical', bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500' },
} as const;

function AnnotationOverlay({ annotation }: { annotation: FormSubmissionPhotoAnnotation }) {
  const cfg = CONDITION_CONFIG[annotation.condition] ?? CONDITION_CONFIG.fair;

  return (
    <div className="absolute bottom-0 left-0 right-0 bg-black/70 backdrop-blur-sm text-white p-4 space-y-2 max-h-56 overflow-y-auto">
      {/* Condition badge */}
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
          <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
          {cfg.label}
        </span>
        <span className="text-[10px] text-white/50">{annotation.model}</span>
      </div>

      {/* Description */}
      {annotation.description && (
        <p className="text-sm text-white/90 leading-relaxed">{annotation.description}</p>
      )}

      {/* Concerns */}
      {annotation.concerns.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold text-white/60 uppercase tracking-wide mb-1">Concerns</p>
          <ul className="space-y-0.5">
            {annotation.concerns.map((concern, i) => (
              <li key={i} className="text-xs text-red-300 flex items-start gap-1.5">
                <span className="mt-1 shrink-0 w-1 h-1 rounded-full bg-red-400" />
                {concern}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Tags */}
      {annotation.tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {annotation.tags.map((tag) => (
            <span
              key={tag}
              className="px-1.5 py-0.5 rounded text-[10px] bg-white/10 text-white/80"
            >
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function ImageLightbox({
  images,
  initialIndex = 0,
  onClose,
  onIndexChange,
  toolbarExtra,
  overlay,
  editing = false,
  editingPanel,
  onImageLoad,
  disableOcr = false,
}: ImageLightboxProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const areaRef = useRef<HTMLDivElement>(null);
  const [textMode, setTextMode] = useState(false);
  const viewport = usePhotoViewport(areaRef, { wheelEnabled: !editing, dragEnabled: !editing && !textMode, ready: images.length > 0 });
  const { zoom, rotation, pan } = viewport;
  const [showMeta, setShowMeta] = useState(false);
  /**
   * The shown photo's decoded size, tagged with its index. Zoom 1 means FIT (Nick, 2026-10-01):
   * a 4000px phone photo opens inside the screen instead of at its natural size with scrollbars.
   * The drawing layer is inset-0 in the box this sizes, so drawings scale with it.
   */
  const [natural, setNatural] = useState<(PhotoSize & { index: number }) | null>(null);
  const [box, setBox] = useState<PhotoSize>(viewingBox);
  const [showAnnotation, setShowAnnotation] = useState(true);
  /** The index whose image has finished loading: the fade-in's trigger. */
  const [loadedIndex, setLoadedIndex] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const activeThumbRef = useRef<HTMLButtonElement>(null);

  // Touch / swipe state
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const touchStartTime = useRef<number | null>(null);

  // Pinch-zoom state
  const pinchStartDist = useRef<number | null>(null);
  const pinchStartZoom = useRef<number>(1);

  // Clamped: a caller whose list shrinks while the viewer is open must not strand it past the end.
  const image = images[Math.min(currentIndex, images.length - 1)];
  const hasMultiple = images.length > 1;
  // Only a photo whose bytes the host has supplied can be read; reading is on demand (textMode).
  const ocr = useOcrText(image?.src && !disableOcr ? { src: image.src, ocrPath: image.ocrPath } : undefined, textMode && !editing && !disableOcr);

  const { reset: resetViewport, zoomIn: zoomInKey, zoomOut: zoomOutKey, fit: fitKey, rotateRight: rotateRightKey, rotateLeft: rotateLeftKey, setZoom: setViewZoom } = viewport;
  /** Zoom that shows the photo's real pixels (null until it has loaded). */
  const actualKey = useCallback(() => {
    const f = natural && natural.index === currentIndex ? fitPhotoSize(natural, rotation, box) : null;
    const z = actualSizeZoom(natural?.w ?? 0, f?.width ?? null);
    if (z) setViewZoom(z);
  }, [natural, currentIndex, rotation, box, setViewZoom]);
  const goTo = useCallback((i: number) => {
    setCurrentIndex(i);
    resetViewport();
    setTextMode(false);
  }, [resetViewport]);

  // Focus moves into the viewer on open and back to whatever opened it on close.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => opener?.focus?.();
  }, []);

  // Re-fit when the window changes size (rotating a phone, resizing a browser).
  useEffect(() => {
    const onResize = () => setBox(viewingBox());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Keep the active thumbnail visible in a long strip.
  useEffect(() => {
    activeThumbRef.current?.scrollIntoView?.({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }, [currentIndex]);

  useEffect(() => {
    onIndexChange?.(currentIndex);
  }, [currentIndex, onIndexChange]);

  const goNext = useCallback(() => {
    setCurrentIndex((i) => (i + 1) % images.length);
    resetViewport();
    setTextMode(false);
  }, [images.length, resetViewport]);

  const goPrev = useCallback(() => {
    setCurrentIndex((i) => (i - 1 + images.length) % images.length);
    resetViewport();
    setTextMode(false);
  }, [images.length, resetViewport]);

  const copyAll = useCallback(async () => {
    const text = ocr.result?.text ?? '';
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked (permissions / insecure context): the words are still selectable by hand.
    }
  }, [ocr.result]);

  const handleDownload = useCallback(() => {
    if (!image?.src) return;
    const a = document.createElement('a');
    a.href = image.src;
    a.download = image.alt || `image-${currentIndex + 1}`;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [image, currentIndex]);

  /**
   * Keys are the viewer's only while it is the top-most modal and focus is not somewhere else:
   * a confirm or map dialog opened from `toolbarExtra` keeps its own Escape and arrows.
   */
  const ownsKey = useCallback((e: KeyboardEvent) => {
    const root = dialogRef.current;
    if (!root) return false;
    const modals = document.querySelectorAll('[aria-modal="true"]');
    if (modals.length > 0 && modals[modals.length - 1] !== root) return false;
    const target = e.target;
    return !(target instanceof Node) || target === document.body || root.contains(target);
  }, []);

  // Escape: caught in the capture phase on window, before any host's document-level listener,
  // and stopped there, so closing the viewer never also closes the panel behind it.
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !ownsKey(e)) return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', handleEscape, true);
    return () => window.removeEventListener('keydown', handleEscape, true);
  }, [onClose, ownsKey]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (editing || !ownsKey(e)) return;
      // Typing into a field a caller put in the toolbar is not navigation.
      const target = e.target as HTMLElement | null;
      if (e.metaKey || e.ctrlKey || e.altKey || target?.closest?.('input, textarea, select, [contenteditable="true"]')) return;
      switch (e.key) {
        case 'ArrowRight': if (hasMultiple) goNext(); break;
        case 'ArrowLeft': if (hasMultiple) goPrev(); break;
        case 'Home': if (hasMultiple) goTo(0); break;
        case 'End': if (hasMultiple) goTo(images.length - 1); break;
        case '+':
        case '=': zoomInKey(); break;
        case '-': zoomOutKey(); break;
        case '0': fitKey(); break;
        case '1': actualKey(); break;
        case 'r': rotateRightKey(); break;
        case 'R': rotateLeftKey(); break;
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [editing, goNext, goPrev, goTo, hasMultiple, images.length, ownsKey, zoomInKey, zoomOutKey, fitKey, actualKey, rotateRightKey, rotateLeftKey]);

  // Focus trap: the viewer is aria-modal and portalled to <body>, outside any host overlay's
  // own trap, so Tab wraps here. Only the wrap-around cases are intercepted.
  const handleTab = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const focusables = focusableWithin(dialogRef.current);
    if (focusables.length === 0) {
      e.preventDefault();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    const inRing = focusables.includes(active as HTMLElement);
    if (e.shiftKey && (active === first || !inRing)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !inRing)) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  // Touch swipe + pinch-zoom handlers (native, no npm packages)
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (editing) return;
    if (e.touches.length === 1 && viewport.touchPanStart(e.touches[0].clientX, e.touches[0].clientY)) {
      // Zoomed in: one finger pans the photo; it is never a swipe to the next one.
      touchStartX.current = null;
      pinchStartDist.current = null;
      return;
    }
    if (e.touches.length === 1) {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
      touchStartTime.current = Date.now();
      pinchStartDist.current = null;
    } else if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchStartDist.current = Math.sqrt(dx * dx + dy * dy);
      pinchStartZoom.current = zoom;
      // Cancel swipe tracking during pinch
      touchStartX.current = null;
    }
  }, [editing, zoom, viewport]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (editing) return;
    if (e.touches.length === 1 && zoom > 1) {
      viewport.touchPanMove(e.touches[0].clientX, e.touches[0].clientY);
      return;
    }
    if (e.touches.length === 2 && pinchStartDist.current !== null) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const ratio = dist / pinchStartDist.current;
      viewport.setZoom(pinchStartZoom.current * ratio);
    }
  }, [editing, viewport, zoom]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    viewport.touchPanEnd();
    if (
      zoom > 1 ||
      editing ||
      textMode ||
      touchStartX.current === null ||
      touchStartY.current === null ||
      touchStartTime.current === null ||
      !hasMultiple
    ) {
      touchStartX.current = null;
      return;
    }

    const changedTouch = e.changedTouches[0];
    const dx = changedTouch.clientX - touchStartX.current;
    const dy = changedTouch.clientY - touchStartY.current;
    const dt = Date.now() - touchStartTime.current;

    // Only trigger swipe if horizontal movement dominates and threshold met
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    const velocity = absDx / dt; // px/ms

    if (absDx > 50 && absDx > absDy && (absDx > 100 || velocity > 0.3)) {
      if (dx < 0) goNext();
      else goPrev();
    }

    touchStartX.current = null;
    touchStartY.current = null;
    touchStartTime.current = null;
  }, [editing, textMode, hasMultiple, goNext, goPrev, zoom, viewport]);

  if (!image) return null;
  const transform = viewTransform(rotation, zoom, pan);
  const fitted = natural && natural.index === currentIndex ? fitPhotoSize(natural, rotation, box) : null;
  const zoomPercent = percentOfActual(zoom, natural && natural.index === currentIndex ? natural.w : null, fitted?.width ?? null);
  const noteNaturalSize = (el: HTMLImageElement) => {
    if (el.naturalWidth > 0 && el.naturalHeight > 0 && (natural?.index !== currentIndex || natural.w !== el.naturalWidth || natural.h !== el.naturalHeight)) {
      setNatural({ index: currentIndex, w: el.naturalWidth, h: el.naturalHeight });
    }
  };

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      tabIndex={-1}
      onKeyDown={handleTab}
      className="fixed inset-0 z-50 flex flex-col bg-black/90 outline-none motion-safe:animate-[fadeIn_150ms_ease-out]"
      onClick={editing ? undefined : onClose}
    >
      {editing && editingPanel}

      {/* Toolbar */}
      <div
        className={`flex items-center justify-between px-4 py-3 shrink-0 z-10 ${editing ? 'hidden' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Counter + Drive link */}
        <div className="flex items-center gap-3">
          {hasMultiple && (
            <span className="text-white text-sm bg-white/10 px-3 py-1 rounded-full">
              {currentIndex + 1} / {images.length}
            </span>
          )}
          {image.driveLink && (
            <a
              href={image.driveLink}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
              title="Open in Drive"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>

        {/* Right toolbar */}
        <div className="flex items-center gap-1">
          {toolbarExtra?.(currentIndex)}
          {image.annotation && (
            <button
              onClick={() => setShowAnnotation((v) => !v)}
              className={`p-2 rounded-full transition-colors ${
                showAnnotation ? 'bg-[#007ACC] text-white' : 'bg-white/10 text-white hover:bg-white/20'
              }`}
              title={showAnnotation ? 'Hide annotation' : 'Show annotation'}
            >
              <Info className="w-5 h-5" />
            </button>
          )}
          {!disableOcr && (
            <button
              onClick={() => setTextMode((v) => !v)}
              disabled={!image.src}
              aria-pressed={textMode}
              className={`p-2 rounded-full transition-colors disabled:opacity-40 ${
                textMode ? 'bg-[#007ACC] text-white' : 'bg-white/10 text-white hover:bg-white/20'
              }`}
              title="Text: select and copy the words on this photo"
              aria-label="Text"
            >
              <ScanText className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={viewport.zoomOut}
            disabled={viewport.atMin}
            className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors disabled:opacity-40"
            title="Zoom out (-)"
            aria-label="Zoom out"
          >
            <ZoomOut className="w-5 h-5" />
          </button>
          <span
            data-testid="lightbox-zoom-level"
            className="min-w-[3.25rem] text-center text-xs tabular-nums text-white/80"
            aria-live="off"
          >
            {zoomPercent !== null ? `${zoomPercent}%` : `${Math.round(zoom * 100)}%`}
          </span>
          <button
            onClick={viewport.zoomIn}
            disabled={viewport.atMax}
            className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors disabled:opacity-40"
            title="Zoom in (+)"
            aria-label="Zoom in"
          >
            <ZoomIn className="w-5 h-5" />
          </button>
          <button
            onClick={fitKey}
            className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            title="Fit to screen (0)"
            aria-label="Fit to screen"
          >
            <Scan className="w-5 h-5" />
          </button>
          <button
            onClick={actualKey}
            disabled={!natural || natural.index !== currentIndex}
            className="px-2 py-1.5 rounded-full bg-white/10 text-white text-xs font-semibold hover:bg-white/20 transition-colors disabled:opacity-40"
            title="Actual size, 100% (1)"
            aria-label="Actual size"
          >
            1:1
          </button>
          <button
            onClick={viewport.rotateLeft}
            className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            title="Rotate left (Shift+R)"
            aria-label="Turn photo 90 degrees counterclockwise"
          >
            <RotateCcw className="w-5 h-5" />
          </button>
          <button
            onClick={viewport.rotateRight}
            className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            title="Rotate right (R)"
            aria-label="Rotate image 90 degrees clockwise"
          >
            <RotateCw className="w-5 h-5" />
          </button>
          <button
            onClick={() => setShowMeta((v) => !v)}
            aria-pressed={showMeta}
            className={`p-2 rounded-full transition-colors ${
              showMeta ? 'bg-[#007ACC] text-white' : 'bg-white/10 text-white hover:bg-white/20'
            }`}
            title="Details"
            aria-label="Details"
          >
            <PanelRight className="w-5 h-5" />
          </button>
          <button
            onClick={handleDownload}
            className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            title="Download"
            aria-label="Download"
          >
            <Download className="w-5 h-5" />
          </button>
          <button
            onClick={onClose}
            className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            title="Close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main image area */}
      <div
        ref={areaRef}
        data-testid="lightbox-area"
        className={`flex-1 flex items-center justify-center relative overflow-hidden ${
          viewport.canPan && !textMode && !editing ? (viewport.dragging ? 'cursor-grabbing' : 'cursor-grab') : ''
        }`}
        onMouseDown={viewport.onMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Prev arrow */}
        {hasMultiple && !editing && (
          <button
            onClick={goPrev}
            aria-label="Previous photo"
            className="absolute left-3 z-10 p-2.5 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}

        {/* Spoken status: ALWAYS mounted (a live region mounted together with its first message is often
            not announced); only its text changes. The visible pill below carries the buttons. */}
        <div role="status" aria-live="polite" data-testid="lightbox-ocr-live" className="sr-only">
          {!textMode || editing
            ? ''
            : ocr.phase === 'running'
              ? 'Photo text: reading'
              : ocr.phase === 'ready'
                ? 'Photo text: found. Use Copy all to copy it.'
                : ocr.phase === 'none'
                  ? 'Photo text: none found'
                  : ocr.phase === 'error'
                    ? 'Photo text: could not read. Retry is available.'
                    : ''}
        </div>

        {/* Text status: always one of reading / found / none / failed — a failure is never "No text found". */}
        {textMode && !editing && (
          <div
            data-testid="lightbox-ocr-status"
            className="absolute top-2 left-1/2 z-10 -translate-x-1/2 flex items-center gap-2 rounded-full bg-black/70 px-3 py-1.5 text-sm text-white"
          >
            {ocr.phase === 'running' && (
              <>
                <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white motion-safe:animate-spin" aria-hidden="true" />
                Reading text...
              </>
            )}
            {ocr.phase === 'ready' && (
              <>
                <span>Drag to select text</span>
                <button
                  onClick={copyAll}
                  className="flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-0.5 hover:bg-white/25"
                >
                  <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                  {copied ? 'Copied' : 'Copy all'}
                </button>
              </>
            )}
            {ocr.phase === 'none' && <span>No text found on this photo</span>}
            {ocr.phase === 'error' && (
              <>
                <span>Could not read text</span>
                <button
                  onClick={ocr.retry}
                  className="flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-0.5 hover:bg-white/25"
                >
                  <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                  Retry
                </button>
              </>
            )}
          </div>
        )}

        {/* Image + annotation overlay */}
        <div className="relative max-w-[90vw] max-h-[calc(100vh-160px)] overflow-visible">
          {/* Sized to the photo itself (w-max), so the drawing layer covers the whole photo even
              when a large copy scrolls inside the viewer. */}
          <div className="relative w-max">
            {/* Keyed by index: a new photo mounts hidden and fades in once loaded. A sharper copy
                of the SAME photo swapping in (thumbnail -> full size) keeps the element, so the
                browser holds the old pixels until the new ones are ready: no flash. */}
            <img
              key={currentIndex}
              ref={(el) => {
                // An image already in the browser cache may finish before onLoad is attached.
                if (el?.complete && el.naturalWidth > 0) {
                  noteNaturalSize(el);
                  if (loadedIndex !== currentIndex) setLoadedIndex(currentIndex);
                  if (image.src) onImageLoad?.(currentIndex, image.src);
                }
              }}
              src={image.src || undefined}
              alt={image.alt || ''}
              onLoad={(e) => {
                noteNaturalSize(e.currentTarget);
                setLoadedIndex(currentIndex);
                if (image.src) onImageLoad?.(currentIndex, image.src);
              }}
              onError={() => setLoadedIndex(currentIndex)}
              data-loaded={loadedIndex === currentIndex ? 'true' : 'false'}
              className={`block ${editing ? '' : viewport.dragging ? 'transition-opacity duration-200' : 'transition-[transform,opacity] duration-200'} motion-reduce:transition-none ${
                loadedIndex === currentIndex ? 'opacity-100' : 'opacity-0'
              }`}
              style={{
                transform,
                transformOrigin: 'center center',
                ...(fitted ? { width: `${fitted.width}px`, height: `${fitted.height}px`, maxWidth: 'none' } : {}),
              }}
              draggable={false}
            />
            {/* Drawings (F-6): the same box and the SAME transform as the photo, so a mark stays on
                the feature it points at through rotate and zoom. Only once the photo has loaded. */}
            {overlay && loadedIndex === currentIndex && (
              <div
                data-testid="lightbox-overlay"
                className={`absolute inset-0 ${editing ? '' : 'transition-transform duration-200'} motion-reduce:transition-none`}
                style={{ transform, transformOrigin: 'center center' }}
              >
                {overlay(currentIndex)}
              </div>
            )}
            {/* Text (OCR): the same box and the SAME transform as the photo, so selection follows zoom/rotate. */}
            {textMode && !editing && ocr.phase === 'ready' && ocr.result && loadedIndex === currentIndex && (
              <div
                data-testid="lightbox-ocr-frame"
                className="absolute inset-0 transition-transform duration-200 motion-reduce:transition-none"
                style={{ transform, transformOrigin: 'center center' }}
              >
                <OcrTextLayer result={ocr.result} />
              </div>
            )}
          </div>
          {loadedIndex !== currentIndex && (
            <div
              data-testid="lightbox-image-loading"
              aria-hidden="true"
              className="absolute inset-0 m-auto h-10 w-10 rounded-full border-2 border-white/20 border-t-white/80 motion-safe:animate-spin"
            />
          )}
          {image.annotation && showAnnotation && (
            <AnnotationOverlay annotation={image.annotation} />
          )}
        </div>

        {showMeta && !editing && (
          <PhotoMetaDrawer
            rows={image.meta ?? []}
            pixels={natural && natural.index === currentIndex ? { w: natural.w, h: natural.h } : null}
            onClose={() => setShowMeta(false)}
          />
        )}

        {/* Next arrow */}
        {hasMultiple && !editing && (
          <button
            onClick={goNext}
            aria-label="Next photo"
            className="absolute right-3 z-10 p-2.5 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>

      {image.caption && (
        <div
          className="shrink-0 px-4 py-2 text-sm text-white/90"
          onClick={(e) => e.stopPropagation()}
        >
          {image.caption}
        </div>
      )}

      {/* Thumbnail strip */}
      {hasMultiple && (
        <div
          className="shrink-0 flex items-center gap-2 px-4 py-3 overflow-x-auto bg-black/30"
          onClick={(e) => e.stopPropagation()}
        >
          {images.map((img, i) => {
            const conditionCfg = img.annotation
              ? CONDITION_CONFIG[img.annotation.condition]
              : null;

            return (
              <button
                key={i}
                ref={i === currentIndex ? activeThumbRef : undefined}
                onClick={() => goTo(i)}
                disabled={editing}
                aria-label={`Show photo ${i + 1}`}
                aria-current={i === currentIndex ? 'true' : undefined}
                className={`relative shrink-0 w-14 h-14 rounded-lg overflow-hidden transition-all ${
                  i === currentIndex
                    ? 'ring-2 ring-[#007ACC] ring-offset-1 ring-offset-black opacity-100'
                    : 'opacity-50 hover:opacity-80'
                }`}
              >
                {img.src ? (
                  <img
                    src={img.src}
                    alt={img.alt || `Image ${i + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  // Bytes not loaded yet (a caller loading lazily): a labelled square, never a blank or broken image.
                  <span
                    data-testid="lightbox-thumb-placeholder"
                    className="flex w-full h-full items-center justify-center bg-white/15 px-0.5 text-center text-[10px] font-semibold leading-tight text-white/90"
                    aria-hidden="true"
                  >
                    {img.thumbLabel || String(i + 1)}
                  </span>
                )}
                {conditionCfg && (
                  <span className={`absolute bottom-0.5 right-0.5 w-2.5 h-2.5 rounded-full border border-black/30 ${conditionCfg.dot}`} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>,
    document.body
  );
}
